'use client'

import PropTypes from 'prop-types'
import MUIFormControl from '@mui/material/FormControl'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIStack from '@mui/material/Stack'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIInput from '@mui/material/TextField'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUITypography from '@mui/material/Typography'
import { SearchIcon } from '@repo/ui/SearchIcon'
import { useState, useEffect } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import { CaretDownIcon } from '@repo/ui/CaretDownIcon'

export const SelectMenu = ({
  size = 'small',
  height = 40,
  label = 'Select',
  disabled = false,
  onChange = () => null,
  labelKey = 'description',
  valueKey = 'id',
  items = [],
  value = null,
  sx = {},
  renderValue = null,
  optionRenderValue = null,
  searchable = false,
  searchFunction = null,
  onOpen = null,
  insertOptionFunction = null,
  idKey = null,
  required = false
}) => {
  const [searchCriteria, setSearchCriteria] = useState('')
  const debouncedSearchCriteria = useDebounce(searchCriteria, 400)
  const [previousDebouncedSearchCriteria, setPreviousDebouncedSearchCriteria] = useState(searchCriteria)
  const [filteredItems, setFilteredItems] = useState(items)

  // we use the menuIsOpen state to optimise performance, only rendering the menu when it is open
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  useEffect(() => {
    if (!searchable || searchFunction) return

    // if no search function is provided, use s simple javascript search

    if (!searchCriteria) {
      setFilteredItems(items)
      return
    }

    const lowerSearchCriteria = searchCriteria.toLowerCase()
    const filteredItems = items.map((item) => {
      const newItem = { ...item }
      const lowerLabel = newItem[labelKey].toLowerCase()
      const lowerValue = valueKey ? String(newItem[valueKey]).toLowerCase() : ''
      const lowerId = idKey ? String(newItem[idKey]).toLowerCase() : ''
      const match =
        lowerLabel.includes(lowerSearchCriteria) ||
        lowerValue.includes(lowerSearchCriteria) ||
        lowerId.includes(lowerSearchCriteria)
      newItem.hidden = !match
      return newItem
    })

    setFilteredItems(filteredItems)
  }, [searchable, searchFunction, searchCriteria, items, labelKey, valueKey, idKey])

  useEffect(() => {
    if (!searchable || !searchFunction) return

    // only trigger on change
    if (debouncedSearchCriteria === previousDebouncedSearchCriteria) return

    searchFunction(debouncedSearchCriteria)
    setPreviousDebouncedSearchCriteria(debouncedSearchCriteria)
  }, [searchable, searchFunction, debouncedSearchCriteria, previousDebouncedSearchCriteria])

  useEffect(() => {
    setFilteredItems(items)
  }, [items])

  const handleOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget)
      setMenuIsOpen(true)
      if (onOpen) onOpen()
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
    setMenuIsOpen(false)
    setSearchCriteria('')
  }

  const handleSelect = (item) => {
    if (onChange && menuIsOpen) {
      onChange(item)
    }

    setAnchorEl(null)
    setMenuIsOpen(false)
  }

  const defaultRenderValue = (item, isListItem = false) => {
    const idIdentifier = idKey || labelKey
    const id = item && idIdentifier ? item[idIdentifier] : ''
    const label = item && labelKey ? item[labelKey] : ''

    return (
      <MUIStack direction="column" sx={{ width: '100%' }}>
        {id && isListItem ? (
          <MUITypography
            sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none', color: 'text.secondary' }}
            variant="body2"
            noWrap
          >
            {id.toUpperCase()}
          </MUITypography>
        ) : null}
        {label ? (
          <MUITypography sx={{ mb: 0, userSelect: 'none', pointerEvents: 'none' }} variant="body1" noWrap>
            {label}
          </MUITypography>
        ) : null}
      </MUIStack>
    )
  }

  return (
    <MUIFormControl sx={{ m: 0, maxHeight: height, height, ...sx }} size={size}>
      <MUIInputLabel
        id="select-label"
        sx={{
          pointerEvents: 'none',
          backgroundColor: disabled ? 'rgb(242, 242, 242)' : 'white',
          color: disabled ? 'text.disabled' : 'rgba(5, 28, 51, 0.60)',
          '&.MuiInputLabel-shrink': {
            background: disabled ? 'linear-gradient(0deg, rgb(242, 242, 242), white) !important' : 'white !important'
          }
        }}
        shrink={!!value || menuIsOpen}
      >
        {label}
        {required ? <span style={{ color: '#C33241' }}> *</span> : ''}
      </MUIInputLabel>
      <MUIStack
        onClick={handleOpen}
        direction="row"
        alignItems="center"
        data-testid="SelectMenu"
        sx={{
          height: height + 'px',
          borderStyle: 'solid',
          borderRadius: 1,
          borderWidth: menuIsOpen ? '2px' : '1px',
          borderColor: menuIsOpen ? 'text.primary' : disabled ? 'rgba(230, 230, 230)' : 'rgba(0, 0, 0, 0.23)',
          padding: '16px 34px 16px 14px',
          cursor: disabled ? 'not-allowed !important' : 'pointer !important',
          minWidth: '140px',
          opacity: disabled ? 1 : 1,
          backgroundColor: disabled ? 'rgb(242, 242, 242)' : 'white',
          ...sx
        }}
      >
        {renderValue ? renderValue(value) : defaultRenderValue(value)}
        <input
          value={value && valueKey ? value[valueKey] || '' : ''}
          required={required}
          aria-label={label || 'form-field'}
          onChange={() => {}}
          style={{
            opacity: 0,
            pointerEvents: 'none',
            width: '100%',
            height: '40px',
            position: 'absolute',
            left: 0
          }}
        />
      </MUIStack>
      <CaretDownIcon
        sx={{
          color: disabled ? 'rgba(5, 28, 51, 0.30)' : 'rgba(5, 28, 51, 0.60)',
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: `translateY(-50%) rotate(${menuIsOpen ? '-180' : '0'}deg)`,
          pointerEvents: 'none',
          transition: 'transform 0.3s'
        }}
      />
      <MUIMenu
        anchorEl={anchorEl}
        open={menuIsOpen}
        onClose={handleClose}
        sx={{
          '.MuiPaper-root': {
            paddingTop: searchable ? 0 : '8px',
            display: menuIsOpen ? 'block' : 'none',
            width: '400px',
            maxWidth: '400px',
            height: '400px',
            backgroundColor: 'rgba(250, 250, 250)'
          },
          '.MuiList-root': { position: 'relative' }
        }}
      >
        {renderSearchInput(searchable, searchCriteria, setSearchCriteria)}
        {insertOptionFunction ? insertOptionFunction() : null}
        {filteredItems.map((item, index) => (
          <MUIMenuItem
            onClick={() => handleSelect(item)}
            key={`${item[valueKey]}-${index}`}
            value={item[valueKey]}
            sx={{
              width: '384px',
              minWidth: '384px',
              maxWidth: '384px',
              display: item.hidden ? 'none' : 'flex',
              opacity: item.disabled ? 0.5 : 1,
              pointerEvents: item.disabled ? 'none !important' : 'all',
              border: '1px solid rgba(61, 81, 123, 0.08)',
              mb: '3px !important',
              backgroundColor: item[valueKey] === value[valueKey] ? 'secondary.focus' : 'white'
            }}
          >
            {optionRenderValue ? optionRenderValue(item) : defaultRenderValue(item, true)}
          </MUIMenuItem>
        ))}

        {!filteredItems?.length ? 'No results' : null}
      </MUIMenu>
    </MUIFormControl>
  )
}

const renderSearchInput = (searchable, searchCriteria, setSearchCriteria) => {
  if (!searchable) return null

  return (
    <MUIInput
      placeholder="Search"
      type="text"
      sx={{
        width: '100%',
        minWidth: '384px',
        maxWidth: '384px',
        height: '40px',
        mb: 3,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'white',
        paddingTop: '8px',
        '.MuiInputBase-root': { m: 0, minHeight: '40px', backgroundColor: 'white' }
      }}
      value={searchCriteria}
      onChange={(event) => setSearchCriteria(event.target.value)}
      onKeyUp={(event) => {
        event.stopPropagation()
      }}
      onKeyDown={(event) => {
        event.stopPropagation()
      }}
      InputProps={{
        startAdornment: (
          <MUIInputAdornment position="start">
            <SearchIcon sx={{ width: '16px', height: '16px' }} />
          </MUIInputAdornment>
        ),
        onClick: (event) => {
          event.stopPropagation()
        }
      }}
    />
  )
}

SelectMenu.propTypes = {
  size: PropTypes.string,
  height: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  items: PropTypes.array,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  idKey: PropTypes.string,
  sx: PropTypes.object,
  renderValue: PropTypes.func,
  optionRenderValue: PropTypes.func,
  searchable: PropTypes.bool,
  searchFunction: PropTypes.func,
  onOpen: PropTypes.func,
  insertOptionFunction: PropTypes.func,
  required: PropTypes.bool
}
